import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Download, CheckCircle, AlertCircle, XCircle, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { logger } from '../../utils/logger';
import { caymanToday } from '../../utils/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ReadinessReports = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [relocations, setRelocations] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedRelocation, setSelectedRelocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [reportData, setReportData] = useState({
    pet_name: '',
    pet_type: 'Dog',
    pet_breed: '',
    owner_name: '',
    assessment_date: caymanToday(),
    destination_country: '',
    readiness_status: 'Needs Attention',
    
    health_check: {
      status: 'Pending',
      notes: ''
    },
    
    vaccinations: {
      status: 'Pending',
      required: [],
      completed: []
    },
    
    microchip: {
      status: 'Not Verified',
      number: ''
    },
    
    travel_crate: {
      status: 'Not Assessed',
      notes: ''
    },
    
    documents: [],
    recommendations: [],
    next_steps: []
  });

  const [newVaccine, setNewVaccine] = useState('');
  const [newDocument, setNewDocument] = useState({ name: '', status: 'Pending', notes: '' });
  const [newRecommendation, setNewRecommendation] = useState('');
  const [newStep, setNewStep] = useState('');

  useEffect(() => {
    fetchClients();
    fetchRelocations();
  }, []);

  const fetchClients = async () => {
    try {
      const auth = btoa('admin:Pawsome2026!');
      const response = await fetch(`${API_URL}/api/admin/clients`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      logger.error('Error fetching clients:', error);
    }
  };

  const fetchRelocations = async () => {
    try {
      const auth = btoa('admin:Pawsome2026!');
      const response = await fetch(`${API_URL}/api/admin/relocations`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRelocations(data);
      }
    } catch (error) {
      logger.error('Error fetching relocations:', error);
    }
  };

  const handleClientSelect = (clientId) => {
    setSelectedClient(clientId);
    const client = clients.find(c => c.id === clientId);
    if (client) {
      setReportData(prev => ({
        ...prev,
        owner_name: `${client.first_name} ${client.last_name}`,
        client_id: clientId,  // Store client ID
        client_email: client.email  // Store client email for easy sending
      }));
    }
  };

  const handleRelocationSelect = (relocationId) => {
    setSelectedRelocation(relocationId);
    const relocation = relocations.find(r => r.id === relocationId);
    if (relocation) {
      setReportData(prev => ({
        ...prev,
        pet_name: relocation.pet_name || '',
        pet_type: relocation.pet_type || 'Dog',
        pet_breed: relocation.pet_breed || '',
        destination_country: relocation.destination || '',
        relocation_id: relocationId  // Store relocation ID
      }));
    }
  };

  const addVaccine = (type) => {
    if (!newVaccine.trim()) return;
    
    setReportData(prev => ({
      ...prev,
      vaccinations: {
        ...prev.vaccinations,
        [type]: [...prev.vaccinations[type], newVaccine.trim()]
      }
    }));
    setNewVaccine('');
  };

  const removeVaccine = (type, index) => {
    setReportData(prev => ({
      ...prev,
      vaccinations: {
        ...prev.vaccinations,
        [type]: prev.vaccinations[type].filter((_, i) => i !== index)
      }
    }));
  };

  const addDocument = () => {
    if (!newDocument.name.trim()) return;
    
    setReportData(prev => ({
      ...prev,
      documents: [...prev.documents, { ...newDocument }]
    }));
    setNewDocument({ name: '', status: 'Pending', notes: '' });
  };

  const removeDocument = (index) => {
    setReportData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const addRecommendation = () => {
    if (!newRecommendation.trim()) return;
    setReportData(prev => ({
      ...prev,
      recommendations: [...prev.recommendations, newRecommendation.trim()]
    }));
    setNewRecommendation('');
  };

  const addNextStep = () => {
    if (!newStep.trim()) return;
    setReportData(prev => ({
      ...prev,
      next_steps: [...prev.next_steps, newStep.trim()]
    }));
    setNewStep('');
  };

  const generateReport = async () => {
    if (!reportData.pet_name || !reportData.owner_name) {
      toast.error('Please fill in pet name and owner name');
      return;
    }

    setGenerating(true);
    try {
      const response = await fetch(`${API_URL}/api/travelsafe/readiness-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reportData)
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `TravelSafe_Report_${reportData.pet_name.replace(' ', '_')}_${caymanToday()}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success('Readiness report generated and saved to database!');
        
        // Ask if they want to email it
        if (reportData.client_email) {
          const shouldEmail = window.confirm(`Would you like to email this report to ${reportData.owner_name} (${reportData.client_email})?`);
          if (shouldEmail) {
            await emailReportToClient();
          }
        }
      } else {
        toast.error('Failed to generate report');
      }
    } catch (error) {
      logger.error('Error generating report:', error);
      toast.error('Error generating report');
    } finally {
      setGenerating(false);
    }
  };

  const emailReportToClient = async () => {
    if (!reportData.client_email) {
      toast.error('No client email available');
      return;
    }

    try {
      // Get the latest report ID from database (we just created it)
      const auth = btoa('admin:Pawsome2026!');
      const reportsResponse = await fetch(`${API_URL}/api/admin/readiness-reports`, {
        headers: { 'Authorization': `Basic ${auth}` }
      });
      
      if (reportsResponse.ok) {
        const reports = await reportsResponse.json();
        const latestReport = reports[0]; // Most recent report
        
        if (latestReport) {
          const emailResponse = await fetch(`${API_URL}/api/admin/readiness-reports/${latestReport.id}/email`, {
            method: 'POST',
            headers: {
              'Authorization': `Basic ${auth}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              to_email: reportData.client_email,
              message: 'Please find attached your TravelSafe Readiness Report. Review the assessment and contact us if you have any questions.'
            })
          });
          
          if (emailResponse.ok) {
            toast.success(`Report emailed to ${reportData.client_email}`);
          } else {
            toast.error('Failed to email report');
          }
        }
      }
    } catch (error) {
      logger.error('Error emailing report:', error);
      toast.error('Error emailing report');
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'Complete' || status === 'Verified' || status === 'Approved' || status === 'Ready') {
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    } else if (status.includes('Attention') || status.includes('Progress') || status.includes('Partial')) {
      return <AlertCircle className="w-5 h-5 text-orange-600" />;
    } else {
      return <XCircle className="w-5 h-5 text-red-600" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">TravelSafe Readiness Reports</h1>
                <p className="text-sm text-gray-600">Generate comprehensive pet travel readiness assessments</p>
              </div>
            </div>
            <Button
              onClick={() => navigate('/admin/reports-history')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <History className="w-4 h-4" />
              View History
            </Button>
          </div>
        </div>

        {/* Quick Select */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Select (Optional)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Client</label>
              <select
                value={selectedClient}
                onChange={(e) => handleClientSelect(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Select Client --</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.first_name} {client.last_name} - {client.email}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Relocation</label>
              <select
                value={selectedRelocation}
                onChange={(e) => handleRelocationSelect(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Select Relocation --</option>
                {relocations.map(relocation => (
                  <option key={relocation.id} value={relocation.id}>
                    {relocation.pet_name} → {relocation.destination}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Report Form */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Pet Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pet Name *</label>
              <input
                type="text"
                value={reportData.pet_name}
                onChange={(e) => setReportData({...reportData, pet_name: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Max"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pet Type</label>
              <select
                value={reportData.pet_type}
                onChange={(e) => setReportData({...reportData, pet_type: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="Dog">Dog</option>
                <option value="Cat">Cat</option>
                <option value="Bird">Bird</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Breed</label>
              <input
                type="text"
                value={reportData.pet_breed}
                onChange={(e) => setReportData({...reportData, pet_breed: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Labrador Retriever"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Owner Name *</label>
              <input
                type="text"
                value={reportData.owner_name}
                onChange={(e) => setReportData({...reportData, owner_name: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Assessment Date</label>
              <input
                type="date"
                value={reportData.assessment_date}
                onChange={(e) => setReportData({...reportData, assessment_date: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Destination Country</label>
              <input
                type="text"
                value={reportData.destination_country}
                onChange={(e) => setReportData({...reportData, destination_country: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., United Kingdom"
              />
            </div>
          </div>

          {/* Overall Status */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Overall Readiness Status</label>
            <div className="flex gap-4">
              {['Ready', 'Needs Attention', 'Not Ready'].map(status => (
                <label key={status} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="readiness_status"
                    value={status}
                    checked={reportData.readiness_status === status}
                    onChange={(e) => setReportData({...reportData, readiness_status: e.target.value})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="flex items-center gap-1">
                    {getStatusIcon(status)}
                    {status}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Health Check */}
          <div className="border-t pt-6 mb-6">
            <h3 className="text-md font-semibold text-gray-900 mb-3">Health Check</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={reportData.health_check.status}
                  onChange={(e) => setReportData({
                    ...reportData,
                    health_check: {...reportData.health_check, status: e.target.value}
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Complete">Complete</option>
                  <option value="Needs Attention">Needs Attention</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <input
                  type="text"
                  value={reportData.health_check.notes}
                  onChange={(e) => setReportData({
                    ...reportData,
                    health_check: {...reportData.health_check, notes: e.target.value}
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Health check details..."
                />
              </div>
            </div>
          </div>

          {/* Vaccinations */}
          <div className="border-t pt-6 mb-6">
            <h3 className="text-md font-semibold text-gray-900 mb-3">Vaccinations</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Vaccination Status</label>
              <select
                value={reportData.vaccinations.status}
                onChange={(e) => setReportData({
                  ...reportData,
                  vaccinations: {...reportData.vaccinations, status: e.target.value}
                })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="Complete">Complete</option>
                <option value="Partial">Partial</option>
                <option value="Pending">Pending</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Required Vaccines</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newVaccine}
                    onChange={(e) => setNewVaccine(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addVaccine('required')}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Rabies"
                  />
                  <Button onClick={() => addVaccine('required')}>Add</Button>
                </div>
                <div className="space-y-1">
                  {reportData.vaccinations.required.map((vaccine, i) => (
                    <div key={i} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <span className="text-sm">{vaccine}</span>
                      <button onClick={() => removeVaccine('required', i)} className="text-red-600 text-sm">Remove</button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Completed Vaccines</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newVaccine}
                    onChange={(e) => setNewVaccine(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addVaccine('completed')}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Rabies"
                  />
                  <Button onClick={() => addVaccine('completed')}>Add</Button>
                </div>
                <div className="space-y-1">
                  {reportData.vaccinations.completed.map((vaccine, i) => (
                    <div key={i} className="flex items-center justify-between bg-green-50 px-3 py-2 rounded">
                      <span className="text-sm">{vaccine}</span>
                      <button onClick={() => removeVaccine('completed', i)} className="text-red-600 text-sm">Remove</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Microchip */}
          <div className="border-t pt-6 mb-6">
            <h3 className="text-md font-semibold text-gray-900 mb-3">Microchip</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={reportData.microchip.status}
                  onChange={(e) => setReportData({
                    ...reportData,
                    microchip: {...reportData.microchip, status: e.target.value}
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Verified">Verified</option>
                  <option value="Not Verified">Not Verified</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Microchip Number</label>
                <input
                  type="text"
                  value={reportData.microchip.number}
                  onChange={(e) => setReportData({
                    ...reportData,
                    microchip: {...reportData.microchip, number: e.target.value}
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="15-digit number"
                />
              </div>
            </div>
          </div>

          {/* Travel Crate */}
          <div className="border-t pt-6 mb-6">
            <h3 className="text-md font-semibold text-gray-900 mb-3">Travel Crate</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={reportData.travel_crate.status}
                  onChange={(e) => setReportData({
                    ...reportData,
                    travel_crate: {...reportData.travel_crate, status: e.target.value}
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Approved">Approved</option>
                  <option value="Needs Adjustment">Needs Adjustment</option>
                  <option value="Not Assessed">Not Assessed</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <input
                  type="text"
                  value={reportData.travel_crate.notes}
                  onChange={(e) => setReportData({
                    ...reportData,
                    travel_crate: {...reportData.travel_crate, notes: e.target.value}
                  })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Crate specifications..."
                />
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="border-t pt-6 mb-6">
            <h3 className="text-md font-semibold text-gray-900 mb-3">Required Documents</h3>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <input
                type="text"
                value={newDocument.name}
                onChange={(e) => setNewDocument({...newDocument, name: e.target.value})}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Document name"
              />
              <select
                value={newDocument.status}
                onChange={(e) => setNewDocument({...newDocument, status: e.target.value})}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="Complete">Complete</option>
                <option value="In Progress">In Progress</option>
                <option value="Pending">Pending</option>
              </select>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newDocument.notes}
                  onChange={(e) => setNewDocument({...newDocument, notes: e.target.value})}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Notes"
                />
                <Button onClick={addDocument}>Add</Button>
              </div>
            </div>
            <div className="space-y-2">
              {reportData.documents.map((doc, i) => (
                <div key={i} className="flex items-center justify-between bg-gray-50 px-4 py-3 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium">{doc.name}</div>
                    <div className="text-sm text-gray-600">Status: {doc.status} • {doc.notes}</div>
                  </div>
                  <button onClick={() => removeDocument(i)} className="text-red-600 text-sm">Remove</button>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div className="border-t pt-6 mb-6">
            <h3 className="text-md font-semibold text-gray-900 mb-3">Recommendations</h3>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newRecommendation}
                onChange={(e) => setNewRecommendation(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addRecommendation()}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Add a recommendation..."
              />
              <Button onClick={addRecommendation}>Add</Button>
            </div>
            <ul className="space-y-2">
              {reportData.recommendations.map((rec, i) => (
                <li key={i} className="flex items-start justify-between bg-blue-50 px-4 py-2 rounded">
                  <span className="text-sm">• {rec}</span>
                  <button onClick={() => setReportData({...reportData, recommendations: reportData.recommendations.filter((_, idx) => idx !== i)})} className="text-red-600 text-sm ml-2">Remove</button>
                </li>
              ))}
            </ul>
          </div>

          {/* Next Steps */}
          <div className="border-t pt-6">
            <h3 className="text-md font-semibold text-gray-900 mb-3">Next Steps</h3>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newStep}
                onChange={(e) => setNewStep(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addNextStep()}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Add a next step..."
              />
              <Button onClick={addNextStep}>Add</Button>
            </div>
            <ol className="space-y-2">
              {reportData.next_steps.map((step, i) => (
                <li key={i} className="flex items-start justify-between bg-green-50 px-4 py-2 rounded">
                  <span className="text-sm">{i + 1}. {step}</span>
                  <button onClick={() => setReportData({...reportData, next_steps: reportData.next_steps.filter((_, idx) => idx !== i)})} className="text-red-600 text-sm ml-2">Remove</button>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Generate Button */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <Button
            onClick={generateReport}
            disabled={generating || !reportData.pet_name || !reportData.owner_name}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg font-semibold"
          >
            {generating ? (
              'Generating Report...'
            ) : (
              <>
                <Download className="w-5 h-5 mr-2" />
                Generate & Download PDF Report
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReadinessReports;
